/**
 * PlaygroundSessionsModal
 *
 * Lista sessões de teste salvas do playground (AgentSimulator), permite
 * preview, replay sequencial e gerenciamento (delete individual ou limpar
 * tudo). Aberto pelo botão "Sessões salvas" no header do simulator e
 * automaticamente quando o backend retorna 409 (limite de 10 atingido)
 * no arquivamento durante "Limpar memória do agente".
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  IconHistory,
  IconPlayerPlay,
  IconTrash,
  IconAlertCircle,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';

import { useAgentsFlowApi } from '../hooks/useAgentsFlowApi';

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PlaygroundSessionsModal = ({ show, onHide, agentId, onReplay, isReplaying }) => {
  const {
    listPlaygroundSessions,
    deletePlaygroundSession,
    clearAllPlaygroundSessions,
  } = useAgentsFlowApi();

  const [sessions, setSessions] = useState([]);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listPlaygroundSessions(agentId);
      setSessions(data.sessions || []);
      setLimit(data.limit || 10);
    } catch (e) {
      setError('Não foi possível carregar as sessões salvas.');
    } finally {
      setLoading(false);
    }
  }, [agentId, listPlaygroundSessions]);

  useEffect(() => {
    if (show) refresh();
  }, [show, refresh]);

  const handleDelete = useCallback(async (sessionId) => {
    const confirm = await Swal.fire({
      title: 'Apagar esta sessão?',
      text: 'Esta ação não pode ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, apagar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deletePlaygroundSession(agentId, sessionId);
      await refresh();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Não foi possível apagar a sessão.' });
    }
  }, [agentId, deletePlaygroundSession, refresh]);

  const handleClearAll = useCallback(async () => {
    const confirm = await Swal.fire({
      title: 'Apagar todas as sessões?',
      text: `${sessions.length} sessão(ões) serão apagadas permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, apagar todas',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;
    try {
      await clearAllPlaygroundSessions(agentId);
      await refresh();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Não foi possível limpar as sessões.' });
    }
  }, [agentId, sessions.length, clearAllPlaygroundSessions, refresh]);

  const handleReplay = useCallback(async (session) => {
    if (typeof onReplay !== 'function') return;
    onHide();
    await onReplay(session.id);
  }, [onReplay, onHide]);

  const count = sessions.length;
  const limitReached = count >= limit;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <IconHistory size={20} />
          Sessões de teste salvas
          <span className="ms-2 text-muted" style={{ fontSize: 14, fontWeight: 400 }}>
            ({count}/{limit})
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {limitReached && (
          <div
            className="d-flex align-items-start gap-2 p-3 mb-3 rounded"
            style={{ background: '#fef3c7', border: '1px solid #f59e0b' }}
          >
            <IconAlertCircle size={20} color="#92400e" />
            <div style={{ color: '#78350f', fontSize: 14 }}>
              Limite de {limit} sessões salvas atingido. Para arquivar a próxima
              sessão ao limpar a memória, apague alguma desta lista primeiro.
            </div>
          </div>
        )}

        {loading && <div className="text-center text-muted py-4">Carregando...</div>}
        {error && <div className="text-center text-danger py-4">{error}</div>}
        {!loading && !error && count === 0 && (
          <div className="text-center text-muted py-5">
            Nenhuma sessão salva ainda. Conversas são arquivadas automaticamente quando
            você clica em <strong>"Limpar memória do agente"</strong>.
          </div>
        )}

        {!loading && !error && count > 0 && (
          <div className="d-flex flex-column gap-2">
            {sessions.map((s) => (
              <div
                key={s.id}
                className="d-flex align-items-start gap-3 p-3 rounded border"
                style={{ background: '#f8fafc' }}
              >
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <strong style={{ fontSize: 14 }}>{s.name || 'Sem título'}</strong>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      · {s.message_count} mensagem(ns) · {formatTimestamp(s.created_at)}
                    </span>
                  </div>
                  <div
                    className="text-muted"
                    style={{
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                    title={s.preview}
                  >
                    {s.preview || '(sem preview)'}
                  </div>
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleReplay(s)}
                    disabled={isReplaying}
                    title="Reexecutar este fluxo no agente"
                  >
                    <IconPlayerPlay size={14} /> Replay
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(s.id)}
                    disabled={isReplaying}
                    title="Apagar esta sessão"
                  >
                    <IconTrash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleClearAll}
          disabled={loading || count === 0}
        >
          Limpar todas
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlaygroundSessionsModal;
